import React, { useMemo } from "react";
import { Autocomplete, TextField, useTheme } from "@mui/material";
import { useField, useFormikContext } from "formik";
import AppCircularProgress from "./AppCircularProgress";

export interface SearchSelectOption {
  value: number | string;
  label: string;
}

interface FormSearchSelectProps {
  name: string;
  label: string;
  options: SearchSelectOption[];
  loading?: boolean;
  onSearchChange?: (text: string) => void;
  onOptionSelect?: (value: number | string) => void;
  filterOptions?: (options: SearchSelectOption[]) => SearchSelectOption[];
}

const FormSearchSelect: React.FC<FormSearchSelectProps> = ({
  name,
  label,
  options,
  loading = false,
  onSearchChange,
  onOptionSelect,
  filterOptions,
}) => {
  const theme = useTheme();
  const [field, meta] = useField<number | string | "">(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const selectedOption = useMemo(() => {
    if (
      field.value === undefined ||
      field.value === null ||
      field.value === ""
    ) {
      return null;
    }

    // 1. Try to find the real option (with the nice label)
    const existingOption = options.find(
      (o) => String(o.value) === String(field.value),
    );
    if (existingOption) return existingOption;

    // 2. If not found in the current list, create a temporary "Placeholder"
    // so the input shows the code instead of being blank.
    return {
      value: field.value,
      label: String(field.value), // Shows "10121500" until the search loads the real name
    };
  }, [field.value, options]);
  return (
    <Autocomplete<SearchSelectOption, false, false, false>
      options={options}
      value={selectedOption}
      loading={loading}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onInputChange={(_, value, reason) => {
        if (reason === "input") {
          onSearchChange?.(value);
        }
      }}
      onChange={(_, option) => {
        setFieldValue(name, option?.value ?? "");
        onOptionSelect?.(option?.value ?? "");
      }}
      onBlur={() => setFieldTouched(name, true)}
      renderInput={(params) => {
        const wrapperProps =
          (params as any).InputProps || (params as any).slotProps?.input || {};

        return (
          <TextField
            {...params}
            fullWidth
            variant="outlined"
            label={label}
            margin="dense"
            error={!!(meta.touched && meta.error)}
            helperText={meta.touched ? meta.error : ""}
            slotProps={{
              ...params.slotProps,
              input: {
                ...wrapperProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <AppCircularProgress /> : null}
                    {wrapperProps.endAdornment}
                  </React.Fragment>
                ),
              },
            }}
          />
        );
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: theme.appSizes.inputHeight,
          borderRadius: theme.appRadius.sm,
        },
      }}
    />
  );
};

export default FormSearchSelect;
